import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Activity, Atom, Beaker, BookOpen, Boxes, ChartNoAxesCombined, ChevronLeft, ChevronRight, ClipboardCheck, Database, Download, FileSearch, FlaskConical, Gauge, History, Menu, Microscope, RefreshCw, Search, Settings, SlidersHorizontal, X } from 'lucide-react'
import type { Health } from '../types'
import { api } from '../api/client'
import { humanValue } from '../lib/labels'
import { Badge, Button, EmptyState, Modal } from './ui'
import { useToast } from './Toast'

const groups = [
  { label: 'Обзор', items: [{ to: '/', label: 'Главная', icon: Gauge }] },
  { label: 'Литература', items: [{ to: '/sources', label: 'Источники', icon: BookOpen }, { to: '/extraction', label: 'Извлечение данных', icon: FileSearch }] },
  { label: 'Данные', items: [{ to: '/experiments', label: 'Эксперименты', icon: FlaskConical }, { to: '/compounds', label: 'Соединения', icon: Atom }, { to: '/results', label: 'Результаты', icon: Beaker }] },
  { label: 'Анализ', items: [{ to: '/analytics', label: 'Аналитика', icon: ChartNoAxesCombined }, { to: '/validation', label: 'Качество данных', icon: ClipboardCheck }] },
  { label: 'Расширенное', items: [{ to: '/kinetics', label: 'Кинетика', icon: Activity }, { to: '/phases', label: 'Фазы', icon: Boxes }, { to: '/measurements', label: 'Измерения', icon: Microscope }, { to: '/features', label: 'Признаки', icon: SlidersHorizontal }, { to: '/audit', label: 'Аудит', icon: History }] },
  { label: 'Система', items: [{ to: '/export', label: 'Экспорт', icon: Download }, { to: '/settings', label: 'Настройки', icon: Settings }] },
]

export function Layout({ health, onLock }: { health: Health; onLock: () => void }) {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const toast = useToast()

  const refresh = () => { window.dispatchEvent(new Event('dcs:refresh')); toast.push('Обновляем текущий раздел', 'info') }

  return <div className={`app-shell ${collapsed ? 'sidebar-collapsed' : ''}`}>
    <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="brand-row"><div className="brand-icon">D</div><div className="brand-copy"><strong>DCS</strong><span>THERMOLYSIS</span></div><button className="mobile-close" onClick={()=>setMobileOpen(false)}><X size={18}/></button></div>
      <nav>{groups.map(group => <div className="nav-group" key={group.label}><div className="nav-group-label">{group.label}</div>{group.items.map(item => {
        const Icon = item.icon
        return <NavLink key={item.to} to={item.to} end={item.to==='/' } onClick={()=>setMobileOpen(false)} className={({isActive})=>`nav-item ${isActive?'active':''}`} title={collapsed ? item.label : undefined}><Icon size={18}/><span>{item.label}</span></NavLink>
      })}</div>)}</nav>
      <button className="collapse-btn" onClick={()=>setCollapsed(v=>!v)}>{collapsed ? <ChevronRight size={17}/> : <><ChevronLeft size={17}/><span>Свернуть</span></>}</button>
    </aside>

    <div className="main-column">
      <header className="topbar">
        <button className="mobile-menu" onClick={()=>setMobileOpen(true)}><Menu size={20}/></button>
        <div className="connection"><span className="status-dot"/><div><strong>{health.db}</strong><span>revision {health.revision} · {health.integrity}</span></div></div>
        <div className="topbar-actions">
          <button className="top-action" onClick={()=>setSearchOpen(true)}><Search size={17}/><span>Поиск</span><kbd>Ctrl K</kbd></button>
          <button className="top-action" onClick={refresh}><RefreshCw size={17}/><span>Обновить</span></button>
          <button className="top-action primary-soft" onClick={()=>navigate('/validation')}><ClipboardCheck size={17}/><span>Проверить данные</span></button>
        </div>
      </header>
      <main className="content-area">
        <AnimatePresence mode="wait"><motion.div key={location.pathname} className="route-frame" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: .18, ease: 'easeOut' }}><Outlet/></motion.div></AnimatePresence>
      </main>
      <footer className="statusbar"><span><Database size={13}/>FastAPI · SQLite/WAL</span><span>Научные правила выполняются сервером</span></footer>
    </div>
    {mobileOpen && <div className="mobile-scrim" onClick={()=>setMobileOpen(false)}/>} 
    <GlobalSearch open={searchOpen} onClose={()=>setSearchOpen(false)} />
    <KeyboardSearch open={()=>setSearchOpen(true)} refresh={refresh}/>
  </div>
}

function KeyboardSearch({ open, refresh }: { open: ()=>void; refresh: ()=>void }) {
  useEffect(() => {
    const handler=(e:KeyboardEvent)=>{
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase()==='k') { e.preventDefault(); open() }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase()==='r') { e.preventDefault(); refresh() }
    }
    window.addEventListener('keydown',handler)
    return () => window.removeEventListener('keydown',handler)
  }, [open, refresh])
  return null
}

function GlobalSearch({ open, onClose }: { open:boolean; onClose:()=>void }) {
  const [q,setQ]=useState('')
  const [rows,setRows]=useState<any[]>([])
  const [busy,setBusy]=useState(false)
  const run=async()=>{if(!q.trim())return;setBusy(true);try{setRows((await api.search(q.trim())).items)}finally{setBusy(false)}}
  return <Modal open={open} onClose={onClose} title="Глобальный поиск" subtitle="Источники, соединения и эксперименты" wide>
    <div className="search-row"><Search size={18}/><input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&void run()} autoFocus placeholder="Например: cobalt, S015, 750 °C…"/><Button variant="primary" onClick={()=>void run()} disabled={!q.trim()||busy}>{busy?'Ищем…':'Найти'}</Button></div>
    <div className="search-results">{rows.length ? rows.map((row,i)=><div className="search-result" key={i}><Badge tone="info">{humanValue(row.kind || row.table || 'record')}</Badge><div><strong>{humanValue(row.label || row.title || row.formula || row.id)}</strong><span>{humanValue(row.subtitle || row.source_id || row.compound_id)}</span></div></div>) : <EmptyState title={q ? 'Ничего не найдено' : 'Введите запрос'} text="Поиск проходит по индексируемым полям реестра."/>}</div>
  </Modal>
}
